import React, { useState, useMemo } from 'react';
import {
    Box, Typography, Container, Paper,
    Grid, Button, Card, CardContent, Divider,
    Alert, Chip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, MenuItem, useTheme
} from '@mui/material';
import {
    CalendarToday as CalendarTodayIcon,
    Add as AddIcon,
    HourglassEmpty as HourglassEmptyIcon,
    Construction as ConstructionIcon,
    Info as InfoIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    Timer as TimerIcon // <-- FIX 1: Missing TimerIcon import
} from '@mui/icons-material';
import moment from 'moment';

// --- MOCK MAINTENANCE DATA ---
const mockMaintenance = [
    {
        id: 1,
        title: 'Q4 Database Patching',
        start: moment().add(3, 'days').set({ hour: 2, minute: 0, second: 0 }).toISOString(),
        end: moment().add(3, 'days').set({ hour: 5, minute: 0, second: 0 }).toISOString(),
        impact: 'Partial Downtime (DB API offline)',
        status: 'Scheduled',
        system: 'Database Cluster',
    },
    {
        id: 2,
        title: 'Weekly Cache Flush',
        start: moment().subtract(1, 'day').set({ hour: 4, minute: 0, second: 0 }).toISOString(),
        end: moment().subtract(1, 'day').set({ hour: 4, minute: 30, second: 0 }).toISOString(),
        impact: 'No Impact (Minor Latency Spike)',
        status: 'Completed',
        system: 'Caching Service',
    },
    {
        id: 3,
        title: 'Emergency Firewall Update',
        start: moment().add(5, 'hours').toISOString(),
        end: moment().add(6, 'hours').toISOString(),
        impact: 'Full Service Outage',
        status: 'In Progress',
        system: 'Network Security',
    },
];

// --- HELPER FUNCTIONS ---

const getStatusChip = (status, theme) => {
    let color;
    let icon;
    switch (status) {
        case 'Scheduled':
            color = theme.palette.info.main;
            icon = <HourglassEmptyIcon style={{ fontSize: 16 }} />;
            break;
        case 'In Progress':
            color = theme.palette.error.main;
            icon = <ConstructionIcon style={{ fontSize: 16 }} />;
            break;
        case 'Completed':
            color = theme.palette.success.main;
            icon = <CheckCircleIcon style={{ fontSize: 16 }} />;
            break;
        default:
            color = theme.palette.grey[500];
            icon = <InfoIcon style={{ fontSize: 16 }} />;
    }

    return (
        <Chip
            label={status}
            size="small"
            icon={icon}
            sx={{
                backgroundColor: color,
                color: 'white',
                fontWeight: 'bold',
                minWidth: 100,
                px: 0.5,
            }}
        />
    );
};

// --- MAINTENANCE CARD COMPONENT ---

const MaintenanceCard = ({ event }) => {
    const theme = useTheme();
    const startTime = moment(event.start);
    const endTime = moment(event.end);
    const duration = moment.duration(endTime.diff(startTime));

    const isCurrent = event.status === 'In Progress';
    const isUpcoming = event.status === 'Scheduled' && startTime.isAfter(moment());

    return (
        <Card
            elevation={isCurrent ? 6 : 3}
            sx={{
                borderLeft: `5px solid ${isCurrent ? theme.palette.error.main : (isUpcoming ? theme.palette.info.main : theme.palette.grey[300])}`,
                mb: 2,
                transition: '0.3s',
                '&:hover': {
                    boxShadow: theme.shadows[isCurrent ? 10 : 6],
                }
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h6" fontWeight={700} color={theme.palette.primary.dark}>
                            {event.title}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                            Target System: **{event.system}**
                        </Typography>
                    </Box>
                    {getStatusChip(event.status, theme)}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
                            <CalendarTodayIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                            Start:
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 2, fontFamily: 'monospace' }}>
                            {startTime.format('YYYY-MM-DD HH:mm')} UTC
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600 }}>
                            <TimerIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                            End:
                        </Typography>
                        <Typography variant="body2" sx={{ ml: 2, fontFamily: 'monospace' }}>
                            {endTime.format('YYYY-MM-DD HH:mm')} UTC
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 600, mt: 1 }}>
                            <ConstructionIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                            Duration & Impact:
                        </Typography>
                        <Typography variant="body2" color={isCurrent ? 'error.dark' : 'textSecondary'} sx={{ ml: 2, fontWeight: 500 }}>
                            {duration.humanize() || 'Unknown'} - {event.impact}
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

// --- SCHEDULE DIALOG COMPONENT ---

const ScheduleMaintenanceDialog = ({ open, handleClose, handleSave }) => {
    const [formData, setFormData] = useState({
        // <-- FIX 2: Corrected unclosed quotes in moment().add()
        title: '',
        start: moment().add(1, 'hour').format('YYYY-MM-DDTHH:mm'),
        end: moment().add(2, 'hour').format('YYYY-MM-DDTHH:mm'),
        impact: 'Partial Downtime',
        system: 'Application Server',
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleInternalSave = () => {
        // Basic validation
        if (!formData.title || moment(formData.start).isSameOrAfter(moment(formData.end))) {
            alert('Please provide a title and ensure the start time is before the end time.');
            return;
        }
        handleSave(formData);
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={700} color="primary">Schedule New Maintenance</Typography>
                <CloseIcon onClick={handleClose} sx={{ cursor: 'pointer', color: 'text.secondary' }} />
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Maintenance Title"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Start Time (UTC)"
                            name="start"
                            type="datetime-local"
                            value={formData.start}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="End Time (UTC)"
                            name="end"
                            type="datetime-local"
                            value={formData.end}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Target System"
                            name="system"
                            value={formData.system}
                            onChange={handleChange}
                        >
                            {['Database Cluster', 'Application Server', 'Caching Service', 'Network Security'].map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            select
                            fullWidth
                            label="Impact Level"
                            name="impact"
                            value={formData.impact}
                            onChange={handleChange}
                        >
                            {['Full Service Outage', 'Partial Downtime', 'Minor Latency Spike', 'No Impact'].map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
                <Alert severity="info" sx={{ mt: 3 }}>
                    Maintenance periods are automatically communicated to users via the system status banner.
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} variant="outlined">Cancel</Button>
                <Button onClick={handleInternalSave} variant="contained" color="primary" startIcon={<AddIcon />}>
                    Schedule Maintenance
                </Button>
            </DialogActions>
        </Dialog>
    );
};


// --- MAIN COMPONENT ---
const ScheduledMaintenance = () => {
    const theme = useTheme();
    const [events, setEvents] = useState(mockMaintenance);
    const [openDialog, setOpenDialog] = useState(false);

    // Sort events by start time (upcoming first)
    const sortedEvents = useMemo(() => {
        return [...events].sort((a, b) => moment(a.start).diff(moment(b.start)));
    }, [events]);

    const handleSaveNewEvent = (newEventData) => {
        const newEvent = {
            ...newEventData,
            id: events.length + 1,
            status: 'Scheduled',
            start: moment(newEventData.start).toISOString(),
            end: moment(newEventData.end).toISOString(),
        };
        setEvents(prev => [...prev, newEvent]);
        setOpenDialog(false);
        alert(`Maintenance ${newEvent.title} successfully scheduled!`);
    };

    const upcomingEvents = sortedEvents.filter(e => e.status === 'Scheduled' || e.status === 'In Progress');
    const pastEvents = sortedEvents.filter(e => e.status === 'Completed').reverse();

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography
                variant="h4"
                component="h1"
                gutterBottom
                sx={{
                    fontWeight: 800,
                    color: theme.palette.primary.dark,
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                }}
            >
                <CalendarTodayIcon sx={{ mr: 1.5, fontSize: '3rem', color: theme.palette.secondary.dark }} />
                System Maintenance Scheduler
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 4 }}>
                Review and schedule planned downtime periods.
            </Typography>

            <Paper elevation={6} sx={{ p: 4, borderRadius: 2 }}>
                
                {/* Header and Action */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" fontWeight={700} color="textPrimary">
                        Upcoming & Current Events ({upcomingEvents.length})
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenDialog(true)}
                    >
                        Schedule New Event
                    </Button>
                </Box>
                
                <Divider sx={{ mb: 3 }} />

                {/* Upcoming Events List */}
                {upcomingEvents.length > 0 ? (
                    <Grid container spacing={3}>
                        {upcomingEvents.map(event => (
                            <Grid item xs={12} md={6} key={event.id}>
                                <MaintenanceCard event={event} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Alert severity="success" sx={{ py: 2 }}>
                        <CheckCircleIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                        No maintenance currently scheduled. The system is operating normally.
                    </Alert>
                )}

                {/* Past Events Section (Collapsible/Minimized view) */}
                {pastEvents.length > 0 && (
                    <Box sx={{ mt: 5 }}>
                        <Typography variant="h6" fontWeight={700} color="textSecondary" sx={{ mb: 2 }}>
                            Past Maintenance History ({pastEvents.length})
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        {/* Only show the last 2 completed events for brevity */}
                        <Grid container spacing={3}>
                            {pastEvents.slice(0, 2).map(event => (
                                <Grid item xs={12} md={6} key={event.id}>
                                    <MaintenanceCard event={event} />
                                </Grid>
                            ))}
                        </Grid>
                        {pastEvents.length > 2 && (
                            <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
                                + {pastEvents.length - 2} more completed events hidden.
                            </Typography>
                        )}
                    </Box>
                )}
                
            </Paper>
            
            {/* New Maintenance Dialog */}
            <ScheduleMaintenanceDialog 
                open={openDialog} 
                handleClose={() => setOpenDialog(false)} 
                handleSave={handleSaveNewEvent} 
            />
        </Container>
    );
};

export default ScheduledMaintenance;